/*
 * Oak & Sprout — basket undo (cart drawer), 15 Oct 2026.
 *
 * When a line leaves the drawer (the card's ×, or its quantity set to 0), the theme removes it exactly as before
 * (theme.js LineItemQuantity → POST /cart/change.js, quantity 0). This file only watches: it remembers the line, and once
 * the drawer has re-rendered without it, puts a slim card in its place: "<title> removed" (title links to the product)
 * and an Undo button. Undo re-adds the same variant, quantity, line-item properties (all of them, "_" ones included) and
 * selling plan via /cart/add.js, then hands the cart to the theme's own cart:change, so totals, delivery, the bar, the
 * bag row and discounts (the BOOK Buy X Get Y) re-render as after any other cart change.
 *
 * One undo at a time (the latest removal). The row stays until the shopper does something else in the drawer: another
 * removal (replaces it), a quantity change, an add, or closing the drawer. No timer.
 *
 * Why a module-level store: Prestige replaces the drawer's children on every cart change (cart-drawer.js
 * replaceContent → cart-drawer:refreshed), so the row is re-inserted from the store after each render.
 *
 * Off on the /cart page: there the theme reloads the page after any change (pageType === 'cart').
 * Loaded once from sections/cart-drawer.liquid by a <script defer> placed after </cart-drawer> (outside the part the
 * theme re-renders). Styles: assets/os-global-overrides.css (.os-undo-row).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.boot(root);
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ---------- pure logic (node-testable) ----------

  // The /cart/add.js body that puts a removed line back as it was.
  function buildReAddPayload(item) {
    var line = { id: item.variant_id, quantity: item.quantity };
    if (item.properties && typeof item.properties === 'object') {
      var props = {};
      var any = false;
      for (var k in item.properties) {
        if (!Object.prototype.hasOwnProperty.call(item.properties, k)) continue;
        var v = item.properties[k];
        if (v === null || v === undefined) continue;
        props[k] = v;
        any = true;
      }
      if (any) line.properties = props;
    }
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (plan && plan.id) line.selling_plan = plan.id;
    return { items: [line] };
  }

  function findItem(cart, key) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === key) return cart.items[i];
    return null;
  }

  // Single-slot store: `pending` = a removal sent but not yet confirmed by the theme's cart:change;
  // `current` = the undo on show.
  function createStore() {
    var s = { pending: null, current: null, snapshot: null };
    s.setSnapshot = function (cart) {
      if (cart && Array.isArray(cart.items)) s.snapshot = cart;
    };
    // A line is about to change. Quantity 0 = a removal: remember it (from the last full cart we saw). Anything else is
    // "something else in the drawer": drop the undo.
    s.willChange = function (key, targetQuantity, index, domTitle, domUrl) {
      if (targetQuantity !== 0) { s.clear(); return null; }
      var item = findItem(s.snapshot, key);
      s.current = null;
      s.pending = {
        key: key,
        index: index,
        title: (item && item.product_title) || domTitle || '',
        url: (item && item.url) || domUrl || '',
        payload: item ? buildReAddPayload(item) : null,
        announced: false
      };
      return s.pending;
    };
    // The theme's cart:change. Returns 'show' when a pending removal is now confirmed, 'clear' when the undo is dropped.
    s.cartChanged = function (detail) {
      var cart = detail && detail.cart;
      var base = detail && detail.baseEvent;
      s.setSnapshot(cart);
      if (s.pending) {
        if (cart && Array.isArray(cart.items) && !findItem(cart, s.pending.key)) {
          s.current = s.pending;
          s.pending = null;
          return 'show';
        }
        return null;
      }
      if (s.current && base !== 'line-item:change') { s.clear(); return 'clear'; }
      return null;
    };
    s.clear = function () { s.pending = null; s.current = null; };
    return s;
  }

  // ---------- browser wiring ----------

  function boot(win) {
    var doc = win.document;
    if (win.__osBasketUndo) return;
    var settings = win.themeVariables && win.themeVariables.settings;
    if (settings && settings.pageType === 'cart') return;
    win.__osBasketUndo = true;

    var store = createStore();
    var root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    var focusAfterRender = null; // 'undo' | a line key to focus after the next render

    function drawer() { return doc.querySelector('cart-drawer'); }

    function refreshSnapshot() {
      try {
        win.fetch(root + 'cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { if (!store.pending) store.setSnapshot(cart); })
          .catch(function () {});
      } catch (e) {}
    }

    function esc(t) {
      return String(t).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function removeRow() {
      var d = drawer();
      var old = d && d.querySelector('.os-undo-row');
      if (old) old.parentNode.removeChild(old);
    }

    function buildRow(u) {
      var row = doc.createElement('div');
      row.className = 'line-item line-item--card os-undo-row';
      row.setAttribute('data-os-undo-key', u.key);
      var title = u.url ? '<a href="' + esc(u.url) + '" class="os-undo-row__title">' + esc(u.title) + '</a>'
                        : '<span class="os-undo-row__title">' + esc(u.title) + '</span>';
      row.innerHTML =
        '<p class="os-undo-row__msg text-sm" role="status" aria-live="polite" id="os-undo-msg"></p>' +
        (u.payload ? '<button type="button" class="os-undo-row__button link text-sm" data-os-undo aria-describedby="os-undo-msg">Undo</button>' : '');
      var msg = row.querySelector('.os-undo-row__msg');
      var fill = function () { msg.innerHTML = title + ' removed'; };
      // Live regions announce changes, not content they were born with: insert empty, fill on the next frame the first
      // time; on re-renders (already announced) fill at once.
      if (u.announced) fill();
      else (win.requestAnimationFrame || win.setTimeout)(function () { fill(); u.announced = true; });
      return row;
    }

    function insertRow() {
      var u = store.current;
      var d = drawer();
      if (!u || !d) return;
      removeRow();
      var row = buildRow(u);
      var list = d.querySelector('.cart-drawer__items');
      if (list) {
        var lines = list.querySelectorAll(':scope > line-item');
        var before = lines[u.index] || list.querySelector(':scope > .os-bag-row');
        if (before) list.insertBefore(row, before); else list.appendChild(row);
      } else {
        // Basket now empty: the drawer body holds only the "empty" message. Sit above it.
        var first = null;
        for (var i = 0; i < d.children.length; i++) {
          if (!d.children[i].hasAttribute('slot')) { first = d.children[i]; break; }
        }
        d.insertBefore(row, first);
      }
      if (focusAfterRender === 'undo') {
        focusAfterRender = null;
        var btn = row.querySelector('[data-os-undo]') || row.querySelector('a');
        if (btn) btn.focus({ preventScroll: false });
      }
    }

    function lineIndex(lineItem) {
      var list = lineItem && lineItem.parentElement;
      if (!list) return 0;
      var lines = list.querySelectorAll(':scope > line-item');
      for (var i = 0; i < lines.length; i++) if (lines[i] === lineItem) return i;
      return lines.length;
    }

    function keyOf(lineItem) {
      var input = lineItem.querySelector('[data-line-key]');
      if (input) return input.getAttribute('data-line-key');
      var a = lineItem.querySelector('a[href*="/cart/change"]');
      if (a) { try { return new URL(a.href, win.location.href).searchParams.get('id'); } catch (e) {} }
      return null;
    }

    // 1. A drawer line is about to change (theme.js dispatches this before its /cart/change.js request).
    doc.addEventListener('line-item:will-change', function (event) {
      try {
        var li = event.target;
        if (!li || !li.closest || !li.closest('cart-drawer')) return;
        var target = event.detail && event.detail.targetQuantity;
        var key = keyOf(li);
        if (!key) return;
        // A merged card's − taking one of its lines to 0 is a quantity change, not a removal: no undo row.
        if (target === 0 && li.querySelector('[data-os-offset]') && !(event.detail && event.detail.osQuantity)) {
          store.clear(); removeRow(); return;
        }
        var a = li.querySelector('.line-item-info a[href]:not([href*="/cart/change"])');
        var p = store.willChange(key, target, lineIndex(li), a ? a.textContent.trim() : '', a ? a.getAttribute('href') : '');
        // A merged card (several lines of one item, cart-drawer.liquid) removes them all: put the whole quantity back.
        var osQty = event.detail && event.detail.osQuantity;
        if (p && p.payload && osQty > 0) p.payload.items[0].quantity = osQty;
        if (!p) removeRow();
        else focusAfterRender = 'undo';
      } catch (e) {}
    });

    // 2. Every cart change (ours, the theme's, bundles, donation, product form).
    doc.addEventListener('cart:change', function (event) {
      try {
        var r = store.cartChanged(event.detail);
        if (r === 'clear') removeRow();
      } catch (e) {}
    });
    doc.addEventListener('cart:refresh', refreshSnapshot);

    // 3. The drawer has re-rendered: put the row back.
    doc.addEventListener('cart-drawer:refreshed', function () {
      try {
        bindDrawer();
        if (store.current) insertRow();
        else if (focusAfterRender && focusAfterRender !== 'undo') {
          var d = drawer();
          var esc2 = win.CSS && win.CSS.escape ? win.CSS.escape(focusAfterRender) : focusAfterRender;
          var input = d && d.querySelector('[data-line-key="' + esc2 + '"]');
          var li = input && input.closest('line-item');
          var link = li && li.querySelector('.line-item-info a[href]');
          if (link) link.focus();
          focusAfterRender = null;
        }
      } catch (e) {}
    });

    // 4. Closing the drawer ends the undo; opening it refreshes what we know of the cart.
    function bindDrawer() {
      var d = drawer();
      if (!d || d.__osUndoBound) return;
      d.__osUndoBound = true;
      d.addEventListener('dialog:before-hide', function () {
        try { store.clear(); removeRow(); focusAfterRender = null; } catch (e) {}
      });
      d.addEventListener('dialog:before-show', function () {
        try { if (!store.pending) refreshSnapshot(); } catch (e) {}
      });
    }

    // 5. Undo.
    doc.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest && event.target.closest('[data-os-undo]');
      if (!btn) return;
      event.preventDefault();
      var u = store.current;
      if (!u || !u.payload || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      doc.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      var sections = [];
      doc.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      var body = { items: u.payload.items, sections: sections.join(',') };
      var added;
      win.fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) throw res.json;
          added = res.json;
          return win.fetch(root + 'cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          cart.sections = added.sections;
          var item = added.items && added.items[0];
          store.clear();
          focusAfterRender = item ? item.key : null;
          doc.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          doc.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'os-basket-undo:add', cart: cart }
          }));
        })
        .catch(function (err) {
          doc.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          btn.removeAttribute('aria-busy');
          var msg = doc.getElementById('os-undo-msg');
          var why = (err && (err.description || err.message)) || 'please try again';
          if (msg) msg.textContent = 'Couldn’t add ' + u.title + ' back: ' + why;
          // Keep the product link on hand (Baymard's fallback).
          if (msg && u.url) {
            msg.appendChild(doc.createTextNode(' '));
            var a = doc.createElement('a');
            a.href = u.url;
            a.className = 'os-undo-row__title';
            a.textContent = 'View product';
            msg.appendChild(a);
          }
          // Refused by the shop (sold out, quantity rule): the button stays off. A network failure: it can be tried again.
          btn.disabled = !!(err && err.description);
        });
    });

    refreshSnapshot();
    bindDrawer();
    if (!drawer()) doc.addEventListener('DOMContentLoaded', bindDrawer);
  }

  return { buildReAddPayload: buildReAddPayload, createStore: createStore, findItem: findItem, boot: boot };
});
